import apiClient from '../api/apiClient';

// Project showcase videos: /ProjectVideo. Records use `id` and `views`.
// Admins see drafts too; everyone else only gets published videos.
const VIDEO_ENDPOINTS = {
  GET_ALL: '/ProjectVideo',
  GET_BY_ID: id => `/ProjectVideo/${id}`,
  CREATE: '/ProjectVideo',
  UPDATE: id => `/ProjectVideo/${id}`,
  DELETE: id => `/ProjectVideo/${id}`,
};

/** The 11-character YouTube id from a watch / youtu.be / embed / shorts URL (or a bare id). */
export const getYouTubeVideoId = url => {
  if (!url) return null;
  const value = String(url).trim();

  if (/^[A-Za-z0-9_-]{11}$/.test(value)) return value;

  const short = value.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
  if (short) return short[1];

  const path = value.match(/youtube(?:-nocookie)?\.com\/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]{11})/);
  if (path) return path[1];

  if (/youtube\.com\/watch/.test(value)) {
    const query = value.match(/[?&]v=([A-Za-z0-9_-]{11})/);
    if (query) return query[1];
  }

  return null;
};

/** The default YouTube thumbnail for a video URL (same one the server falls back to), or null. */
export const getYouTubeThumbnail = url => {
  const id = getYouTubeVideoId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};

// Older records used 2147483647 to mean "unknown"; treat it like 0.
export const LEGACY_UNKNOWN_DURATION = 2147483647;

export const VIDEO_LEVELS = ['Beginner', 'Intermediate', 'Advanced'];

/** "0:00", "5:30" or "1:02:03" for a number of seconds. */
export const formatTimestamp = seconds => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h ? String(m).padStart(2, '0') : String(m);
  return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
};

/** Video length for display, or '' when it isn't known. */
export const formatDuration = seconds => {
  const total = Number(seconds);
  return Number.isFinite(total) && total > 0 && total < LEGACY_UNKNOWN_DURATION
    ? formatTimestamp(total)
    : '';
};

/** Non-empty trimmed lines of a textarea. */
export const toLines = text =>
  String(text || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

/**
 * Chapters typed one per line, "1:20 Admin dashboard" (an optional dash after the time is fine).
 * Returns [{ start, title }], or null when a line can't be read.
 */
export const parseChapters = text => {
  const chapters = [];
  for (const line of toLines(text)) {
    const space = line.search(/\s/);
    if (space === -1) return null;
    const parts = line.slice(0, space).split(':');
    const title = line.slice(space).trim().replace(/^[-–—]\s*/, '');
    if (!title || parts.length < 2 || parts.length > 3) return null;
    if (!parts.every(part => /^\d{1,2}$/.test(part)) || parts[parts.length - 1].length !== 2) return null;

    const [hours, minutes, seconds] = (parts.length === 3 ? parts : ['0', ...parts]).map(Number);
    if (seconds > 59 || (parts.length === 3 && minutes > 59)) return null;
    chapters.push({ start: hours * 3600 + minutes * 60 + seconds, title });
  }
  return chapters;
};

export const chaptersToText = chapters =>
  (chapters || []).map(c => `${formatTimestamp(c.start)} ${c.title}`).join('\n');

/**
 * Admin: products a video can link to, hidden ones included (they get no Buy button until public).
 * GET /Products?IncludeHidden=true&ItemsPerPage=100 -> data.products
 */
export const getVideoProductOptions = async () => {
  const response = await apiClient.get('/Products', {
    params: { IncludeHidden: true, ItemsPerPage: 100 },
  });
  const products = response.data?.data?.products;
  return (Array.isArray(products) ? products : [])
    .filter(p => p?.id)
    .map(p => ({
      id: p.id,
      name: p.name || 'Untitled product',
      price: p.price,
      category: p.category && p.category !== 'Default' ? p.category : '',
      technologies: p.technologies || [],
      features: p.features || [],
      whatInsideTheBox: p.whatInsideTheBox || [],
      isPublic:
        p.isActive !== false && String(p.visibility || '').trim().toLowerCase() === 'public',
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * One page of videos. Resolves with the ApiResponse:
 * { success, data: { items, totalItems, totalPages, currentPage, pageSize } }.
 * filters: { isPublished, searchTerm, sortBy, sortOrder }
 */
export const getAllVideos = async (page = 1, pageSize = 10, filters = {}) => {
  const response = await apiClient.get(VIDEO_ENDPOINTS.GET_ALL, {
    params: {
      page,
      pageSize,
      isPublished: filters.isPublished,
      searchTerm: filters.searchTerm || undefined,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    },
  });
  return response.data;
};

export const getVideoById = async id => {
  const response = await apiClient.get(VIDEO_ENDPOINTS.GET_BY_ID(id));
  return response.data;
};

export const createVideo = async videoData => {
  const response = await apiClient.post(VIDEO_ENDPOINTS.CREATE, videoData);
  return response.data;
};

/** Partial update: fields left out stay as they are. */
export const updateVideo = async (id, videoData) => {
  const response = await apiClient.put(VIDEO_ENDPOINTS.UPDATE(id), videoData);
  return response.data;
};

export const deleteVideo = async id => {
  const response = await apiClient.delete(VIDEO_ENDPOINTS.DELETE(id));
  return response.data;
};

const videoService = {
  getAllVideos,
  getVideoById,
  createVideo,
  updateVideo,
  deleteVideo,
  getYouTubeVideoId,
  getYouTubeThumbnail,
};

export default videoService;
